import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import {
  Mic, Send, ArrowLeft, Clock, Award, MessageSquare,
  CheckCircle, XCircle, AlertCircle, Loader2, Star, RefreshCw,
  User, Bot, Sparkles, Trophy
} from 'lucide-react';
import KariyerLogo from '../components/KariyerLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIInterview = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [status, setStatus] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [questionNumber, setQuestionNumber] = useState(0);
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [finalResult, setFinalResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const response = await axios.get(`${API}/interview/status`, {
        withCredentials: true
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Status check error:', error);
      toast.error('Durum kontrolü başarısız');
    } finally {
      setLoading(false);
    }
  };

  const startInterview = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/interview/start`, {}, {
        withCredentials: true
      });
      
      setSessionId(response.data.session_id);
      setCurrentQuestion(response.data.question);
      setQuestionNumber(response.data.question_number);
      setFeedback(null);
    } catch (error) {
      console.error('Start interview error:', error);
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Mülakat başlatılamadı');
      }
    } finally {
      setLoading(false);
    }
  };

  const submitAnswer = async () => {
    if (!answer.trim()) {
      toast.error('Lütfen bir cevap yazın');
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(`${API}/interview/answer`, {
        session_id: sessionId,
        answer: answer
      }, {
        withCredentials: true
      });

      // Geçmişe ekle
      setHistory(prev => [...prev, {
        question: currentQuestion,
        answer: answer,
        feedback: response.data.feedback,
        score: response.data.score
      }]);

      if (response.data.completed) {
        setCompleted(true);
        setFinalResult(response.data);
      } else {
        setFeedback(response.data);
        setCurrentQuestion(response.data.next_question);
        setQuestionNumber(response.data.question_number);
        setAnswer('');
      }
    } catch (error) {
      console.error('Submit answer error:', error);
      toast.error('Cevap gönderilemedi');
    } finally {
      setSubmitting(false);
    }
  };

  const renderScore = (score) => {
    const stars = [];
    for (let i = 1; i <= 10; i++) {
      stars.push(
        <Star
          key={i}
          className={`w-4 h-4 ${i <= score ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
        />
      );
    }
    return <div className="flex gap-0.5">{stars}</div>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-500 mx-auto" />
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Tamamlanmış mülakat sonuç ekranı
  if (completed && finalResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5 mr-2" />
              Ana Sayfa
            </Button>
            <KariyerLogo size="small" />
          </div>

          {/* Sonuç Kartı */}
          <Card className="border-0 shadow-xl bg-white mb-6">
            <CardContent className="p-8 text-center">
              <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Trophy className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Mülakat Tamamlandı!</h1>
              <p className="text-gray-500 mb-6">Tebrikler, AI mülakat simülasyonunu tamamladınız.</p>
              
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="bg-purple-50 rounded-xl p-4">
                  <p className="text-3xl font-bold text-purple-600">{finalResult.final_score}/10</p>
                  <p className="text-sm text-purple-600">Ortalama Puan</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-3xl font-bold text-blue-600">{finalResult.total_questions}</p>
                  <p className="text-sm text-blue-600">Soru</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4">
                  <p className="text-3xl font-bold text-green-600">
                    {finalResult.final_score >= 7 ? '✅' : finalResult.final_score >= 5 ? '🟡' : '🟠'}
                  </p>
                  <p className="text-sm text-green-600">
                    {finalResult.final_score >= 7 ? 'Başarılı' : finalResult.final_score >= 5 ? 'Geliştirilebilir' : 'Çalışmalı'}
                  </p>
                </div>
              </div>

              {/* Değerlendirme */}
              <div className="bg-gray-50 rounded-xl p-6 text-left mb-6">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  AI Değerlendirmesi
                </h3>
                <p className="text-gray-700 whitespace-pre-wrap">{finalResult.evaluation}</p>
              </div>

              <Button onClick={() => navigate('/dashboard')} className="bg-purple-500 hover:bg-purple-600">
                Ana Sayfaya Dön
              </Button>
            </CardContent>
          </Card>

          {/* Soru Geçmişi */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg">Soru Geçmişi</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {history.map((item, idx) => (
                <div key={idx} className="border-b pb-4 last:border-0">
                  <div className="flex items-start gap-3 mb-2">
                    <Bot className="w-5 h-5 text-purple-500 mt-1" />
                    <p className="text-gray-800 font-medium">{item.question}</p>
                  </div>
                  <div className="flex items-start gap-3 ml-8 mb-2">
                    <User className="w-5 h-5 text-blue-500 mt-1" />
                    <p className="text-gray-600">{item.answer}</p>
                  </div>
                  {item.score && (
                    <div className="ml-8 flex items-center gap-2">
                      <span className="text-sm text-gray-500">Puan:</span>
                      {renderScore(item.score)}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Mülakat başlamadıysa
  if (!sessionId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5 mr-2" />
              Geri
            </Button>
            <KariyerLogo size="small" />
          </div>

          {/* Ana Kart */}
          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <MessageSquare className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">AI Mülakat Simülasyonu</h1>
              <p className="text-gray-500 mb-6">Yapay zeka destekli kabin memuru mülakatına hazırlanın</p>

              {/* Durum Bilgisi */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-600">Aylık Hak:</span>
                  </div>
                  <span className={`font-bold ${status?.can_start ? 'text-green-600' : 'text-red-500'}`}>
                    {status?.interviews_remaining || 0} / 1
                  </span>
                </div>
                {!status?.can_start && (
                  <p className="text-sm text-gray-500 mt-2">
                    Yeni hak: {status?.resets_on}
                  </p>
                )}
              </div>

              {/* Özellikler */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-purple-50 rounded-xl p-4">
                  <Mic className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                  <p className="text-sm text-purple-700">Gerçekçi Sorular</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4">
                  <Award className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                  <p className="text-sm text-blue-700">Anlık Geri Bildirim</p>
                </div>
              </div>

              <Button 
                onClick={startInterview}
                disabled={!status?.can_start || loading}
                className="w-full py-6 text-lg bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : status?.can_start ? (
                  <>
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Mülakatı Başlat
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5 mr-2" />
                    Aylık Hak Doldu
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Mülakat devam ediyor
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="bg-purple-100 px-4 py-2 rounded-full">
              <span className="font-bold text-purple-600">Soru {questionNumber}/5</span>
            </div>
          </div>
          <KariyerLogo size="small" />
        </div>

        {/* Soru Kartı */}
        <Card className="border-0 shadow-xl bg-white mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Bot className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-purple-600 font-medium mb-1">Mülakatçı</p>
                <p className="text-xl text-gray-800 font-medium">{currentQuestion}</p>
              </div>
            </div>

            {/* Geri Bildirim */}
            {feedback && (
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  <span className="font-medium text-purple-700">Geri Bildirim</span>
                  {feedback.score && (
                    <span className="ml-auto bg-purple-100 px-3 py-1 rounded-full text-purple-700 text-sm">
                      {feedback.score}/10
                    </span>
                  )}
                </div>
                <p className="text-gray-700">{feedback.feedback}</p>
                {feedback.tips && feedback.tips.length > 0 && (
                  <ul className="mt-2 text-sm text-gray-600">
                    {feedback.tips.map((tip, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                        {tip}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Cevap Alanı */}
            <div className="space-y-4">
              <Textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Cevabınızı buraya yazın... (İngilizce veya Türkçe)"
                className="min-h-[150px] resize-none"
                disabled={submitting}
              />
              <Button 
                onClick={submitAnswer}
                disabled={submitting || !answer.trim()}
                className="w-full py-4 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Send className="w-5 h-5 mr-2" />
                )}
                Cevabı Gönder
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* İpucu */}
        <div className="bg-white/50 rounded-xl p-4 text-center">
          <p className="text-sm text-gray-600">
            <AlertCircle className="w-4 h-4 inline mr-1" />
            İngilizce cevap vermeniz önerilir. Türkçe de kabul edilir.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIInterview;
